<template>
	<div class="game-ticker-progress-bar">
		<div
			ref="progressBar"
			class="game-ticker-progress-bar__inner"
			id="game-ticker-progress-bar-inner"
			:style="{ width: `${width}%` }"
		/>
	</div>
</template>

<script lang="ts" setup>
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useGameTicker from "@/hooks/useGameTicker";
import { markRaw, onBeforeUnmount, onMounted, ref } from "vue";

const ticker = markRaw(useGameTicker());
const emitter = markRaw(useEmitter());

const width = ref<number>(0);
const progressBar = ref<HTMLElement | null>(null);
let updateInterval: ReturnType<typeof setInterval> | null = null;
let lastProgress = 0;
let newProgress = 0;
let hasReset = false;
let isInResetSequence = false;
let lastResetTime = 0;
let now = 0;
let timeSinceLastReset = 0;

const updateProgress = (): void => {
	newProgress = ticker.progress * 100;

	// Prevent reset detection during reset sequence or too soon after last reset
	now = Date.now();
	timeSinceLastReset = now - lastResetTime;

	// More robust reset detection:
	// 1. Must not be in reset sequence already
	// 2. Must have enough time since last reset (at least 2 seconds)
	// 3. Previous progress must be very high (>98%)
	// 4. New progress must be very low (<5%)
	hasReset =
		!isInResetSequence &&
		timeSinceLastReset > 2000 &&
		lastProgress > 98 &&
		newProgress < 5;

	if (hasReset && progressBar.value) {
		isInResetSequence = true;
		lastResetTime = now;

		// First, ensure we show 100% completion
		progressBar.value.style.transition = "width 0.2s linear";
		width.value = 100;

		// Wait a moment to show the completion, then reset
		setTimeout((): void => {
			if (progressBar.value) {
				// Instantly reset to 0
				progressBar.value.style.transition = "none";
				width.value = 0;

				// Force a reflow to ensure the reset is applied
				void progressBar.value.offsetHeight;

				// Re-enable transition and animate to new value
				setTimeout((): void => {
					if (progressBar.value) {
						progressBar.value.style.transition =
							"width 0.2s linear";
						width.value = newProgress;
						isInResetSequence = false; // Reset sequence complete
					}
				}, 10);
			}
		}, 100); // Show 100% for 100ms
	} else if (!isInResetSequence) {
		// Normal smooth update - only update for meaningful changes and not during reset
		if (Math.abs(width.value - newProgress) > 0.5) {
			width.value = newProgress;
		}
	}

	lastProgress = newProgress;
};

onMounted((): void => {
	// Initial progress update
	updateProgress();

	// Listen to game tick events (primary method)
	emitter.on(EmitterEvents.GameTick, updateProgress);

	// Slower polling for smoother CSS transitions (every 200ms instead of 100ms)
	updateInterval = setInterval(updateProgress, 200);
});

onBeforeUnmount((): void => {
	// Clean up event listener
	emitter.off(EmitterEvents.GameTick, updateProgress);

	// Clean up interval
	if (updateInterval) {
		clearInterval(updateInterval);
		updateInterval = null;
	}
});
</script>

<style lang="scss" scoped>
.game-ticker-progress-bar {
	margin-top: 0.25rem;
	box-shadow: 0 0.063rem 0.063rem 0 rgba(35, 18, 4, 0.6);
	background-color: #0b0b0b;
	width: 100%;
	height: 0.2rem;

	&__inner {
		@include background(
			url($path-images + "backgrounds/progress-bar-fill.avif"),
			auto,
			0 0,
			no-repeat
		);

		// Longer CSS transition for smoother appearance
		transition: width 0.8s linear;
		height: 100%;
	}
}
</style>
